import { z } from "zod";
import { trimmed, uuid } from "./common";

/**
 * Распределение столов между официантами (`core.tables`). Зона — весь филиал, зал или набор столов; в зоне — официанты по порядку.
 * Стол получает первая подходящая зона: «столы» → «зал» → «весь филиал»; в зоне — официант на смене по режиму зоны.
 * Нет никого на смене в зоне — берётся следующая зона; нет нигде — заказ остаётся у того, кто его открыл.
 */
export const WAITER_ZONE_SCOPES = ["branch", "hall", "tables"] as const;
export type WaiterZoneScope = (typeof WAITER_ZONE_SCOPES)[number];
/**
 * priority — первый по порядку, кто на смене (остальные — замена); round_robin — по очереди (кто дольше не получал стол);
 * least_loaded — у кого меньше открытых столов.
 */
export const WAITER_ZONE_MODES = ["priority", "round_robin", "least_loaded"] as const;
export type WaiterZoneMode = (typeof WAITER_ZONE_MODES)[number];

const zoneFields = {
  name: trimmed(1, 80),
  scope: z.enum(WAITER_ZONE_SCOPES),
  hallId: uuid.nullish(),
  tableIds: z.array(uuid).max(200).optional(),
  mode: z.enum(WAITER_ZONE_MODES),
  /** Порядок = приоритет */
  userIds: z.array(uuid).min(1, { error: "Добавьте хотя бы одного официанта" }).max(30).refine((a) => new Set(a).size === a.length, { error: "Официант указан дважды" }),
  isActive: z.boolean().optional(),
};
const refs = <T extends { scope?: WaiterZoneScope; hallId?: string | null; tableIds?: string[] }>(v: T, ctx: z.RefinementCtx) => {
  if (v.scope === "hall" && !v.hallId) ctx.addIssue({ code: "custom", path: ["hallId"], message: "Выберите зал" });
  if (v.scope === "tables" && !v.tableIds?.length) ctx.addIssue({ code: "custom", path: ["tableIds"], message: "Выберите столы" });
};
export const waiterZoneSchema = z.strictObject(zoneFields).superRefine(refs);
export type WaiterZoneInput = z.input<typeof waiterZoneSchema>;
export const waiterZoneUpdateSchema = z.strictObject({ ...zoneFields, version: z.number().int().min(1) }).partial().required({ version: true }).superRefine(refs);

export const waiterSettingsSchema = z.strictObject({
  /** Учитывать только тех, кто отметил приход и не на перерыве (без «Сотрудников и смен» в тарифе — все) */
  requireShift: z.boolean(),
  /** Официант закрыл смену — его открытые столы сразу переходят к следующему по зоне */
  reassignOnLeave: z.boolean(),
  /** Заказ на стол, открытый не официантом зоны (кассир, администратор), получает официант по зоне */
  assignStaffOrders: z.boolean(),
});
export type WaiterSettings = z.output<typeof waiterSettingsSchema>;
export const DEFAULT_WAITER_SETTINGS: WaiterSettings = { requireShift: true, reassignOnLeave: true, assignStaffOrders: true };

/** Официант на стол заказа вручную (push `order.update` → `waiterId`, право `orders.transfer`) — то же поле в REST-форме */
export const orderWaiterSchema = z.strictObject({ userId: uuid });
